// src/components/WorkflowTemplatesPage.js
import React, { useState, useEffect, useCallback } from "react";
import { Workflow, Plus, Pencil, Archive, ArchiveRestore, Star } from "lucide-react";
import Navigation from "./Navigation";
import Card from "./Card";
import LoadingSpinner from "./LoadingSpinner";
import WorkflowTemplateEditor from "./WorkflowTemplateEditor";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";

const errorMessage = (err, fallback) => err?.response?.data?.message || fallback;

/**
 * Settings → Workflows: org-level risk managers and admins manage remediation workflow templates.
 * Route is RISK_MANAGER@ORG (RoleProtectedRoute in App.js); write buttons are gated by
 * useRBAC.canManageWorkflowTemplates, mirroring CanManageOrganizationRisks on the
 * template write endpoints of RemediationWorkflowsController.
 */
const WorkflowTemplatesPage = () => {
    const apiCall = useApi();
    const { canManageWorkflowTemplates } = useRBAC();
    const canManage = canManageWorkflowTemplates();

    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showArchived, setShowArchived] = useState(false);
    const [busyId, setBusyId] = useState(null);
    // null = closed; { template: null } = new; { template } = edit
    const [editor, setEditor] = useState(null);
    const [users, setUsers] = useState(null);
    const [usersLoading, setUsersLoading] = useState(false);
    // Org setting: auto-start the default workflow for new remediations (task 22).
    // { autoEnroll, hasDefaultTemplate } from GET settings; null until loaded.
    const [settings, setSettings] = useState(null);
    const [settingsBusy, setSettingsBusy] = useState(false);
    const [settingsError, setSettingsError] = useState(null);

    // hasDefaultTemplate depends on the template list, so settings reload with it.
    const fetchSettings = useCallback(async () => {
        try {
            const res = await apiCall("GET", "/remediation-workflows/settings");
            setSettings(res && typeof res === "object" ? res : null);
            setSettingsError(null);
        } catch (err) {
            setSettings(null);
            setSettingsError(errorMessage(err, "The automatic workflow setting could not be loaded."));
        }
    }, [apiCall]);

    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await apiCall("GET", `/remediation-workflows/templates?includeArchived=${showArchived}`);
            setTemplates(Array.isArray(res) ? res : []);
        } catch (err) {
            setError(errorMessage(err, "Workflow templates could not be loaded."));
        } finally {
            setLoading(false);
        }
        await fetchSettings();
    }, [apiCall, showArchived, fetchSettings]);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

    const toggleAutoEnroll = async (autoEnroll) => {
        setSettingsBusy(true);
        setSettingsError(null);
        try {
            const res = await apiCall("PUT", "/remediation-workflows/settings", { autoEnroll });
            setSettings(res && typeof res === "object" ? res : { ...settings, autoEnroll });
        } catch (err) {
            setSettingsError(errorMessage(err, "The automatic workflow setting could not be saved."));
        } finally {
            setSettingsBusy(false);
        }
    };

    const ensureUsers = useCallback(async () => {
        if (users !== null) return;
        setUsersLoading(true);
        try {
            const res = await apiCall("GET", "/assessments/assignableUsers");
            setUsers(Array.isArray(res) ? res : []);
        } catch {
            setUsers([]);
        } finally {
            setUsersLoading(false);
        }
    }, [apiCall, users]);

    const openEditor = (template) => {
        setError(null);
        setEditor({ template });
        ensureUsers();
    };

    const runAction = async (id, path, failure) => {
        setBusyId(id);
        setError(null);
        try {
            await apiCall("POST", `/remediation-workflows/templates/${id}/${path}`);
            await fetchTemplates();
        } catch (err) {
            setError(errorMessage(err, failure));
        } finally {
            setBusyId(null);
        }
    };

    const visible = showArchived ? templates : templates.filter((t) => !t.isArchived);

    return (
        <div className="min-h-screen bg-theme-primary">
            <Navigation />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex items-center gap-3 mb-6">
                    <Workflow className="h-7 w-7 text-indigo-600" />
                    <div>
                        <h1 className="text-2xl font-bold text-content-primary">Workflows</h1>
                        <p className="text-sm text-content-secondary">
                            Remediation workflow templates: an ordered list of steps a remediation moves through once a risk manager enrolls it.
                        </p>
                    </div>
                </div>

                {editor && (
                    <div className="mb-6">
                        <WorkflowTemplateEditor
                            key={editor.template?.id ?? "new"}
                            template={editor.template}
                            users={users || []}
                            usersLoading={usersLoading}
                            onSaved={async () => { setEditor(null); await fetchTemplates(); }}
                            onCancel={() => setEditor(null)}
                        />
                    </div>
                )}

                {/* While the editor is open only the editor is shown; the settings card and template
                    list return (and the list refetches) when it closes via onSaved / onCancel. */}
                {!editor && canManage && (
                    <Card className="mb-6">
                        <label className="flex items-start gap-3 text-sm">
                            <input
                                type="checkbox"
                                checked={!!settings?.autoEnroll}
                                disabled={!settings || settingsBusy || (!settings.autoEnroll && !settings.hasDefaultTemplate)}
                                onChange={(e) => toggleAutoEnroll(e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-border text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                            />
                            <span>
                                <span className="font-medium text-content-primary">Automatically start the default workflow for new remediations</span>
                                <span className="block text-xs text-content-secondary mt-0.5">
                                    {!settings
                                        ? "Loading…"
                                        : settings.hasDefaultTemplate || settings.autoEnroll
                                            ? "Applies to remediations created after you turn this on; existing remediations are not enrolled."
                                            : "Set a default template first."}
                                </span>
                            </span>
                        </label>
                        {settingsError && <div className="text-sm text-red-600 mt-2" role="alert">{settingsError}</div>}
                    </Card>
                )}

                {!editor && (
                <Card>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <label className="inline-flex items-center gap-2 text-sm text-content-secondary">
                            <input
                                type="checkbox"
                                checked={showArchived}
                                onChange={(e) => setShowArchived(e.target.checked)}
                                className="h-4 w-4 rounded border-border text-indigo-600 focus:ring-indigo-500"
                            />
                            Show archived
                        </label>
                        {canManage && (
                            <button
                                type="button"
                                onClick={() => openEditor(null)}
                                disabled={!!editor}
                                className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                            >
                                <Plus className="h-4 w-4" /> New template
                            </button>
                        )}
                    </div>

                    {error && <div className="text-sm text-red-600 mb-3" role="alert">{error}</div>}

                    {loading ? (
                        <div className="py-12 flex justify-center"><LoadingSpinner /></div>
                    ) : visible.length === 0 ? (
                        <div className="py-12 text-center text-sm text-content-secondary">
                            No workflow templates yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs font-semibold text-content-secondary uppercase tracking-wide border-b border-border">
                                        <th className="py-2 pr-4">Name</th>
                                        <th className="py-2 pr-4">Steps</th>
                                        <th className="py-2 pr-4">Status</th>
                                        {canManage && <th className="py-2 pr-4 text-right">Actions</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {visible.map((t) => {
                                        const busy = busyId === t.id;
                                        return (
                                            <tr key={t.id} className="border-b border-border hover:bg-theme-hover">
                                                <td className="py-2.5 pr-4">
                                                    <div className="font-medium text-content-primary">{t.name}</div>
                                                    {t.description && <div className="text-xs text-content-secondary">{t.description}</div>}
                                                </td>
                                                <td className="py-2.5 pr-4 text-content-secondary whitespace-nowrap">
                                                    {(t.steps || []).length} {(t.steps || []).length === 1 ? "step" : "steps"}
                                                </td>
                                                <td className="py-2.5 pr-4 whitespace-nowrap">
                                                    <div className="flex items-center gap-1.5">
                                                        {t.isDefault && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                                                                <Star className="h-3 w-3" /> Default
                                                            </span>
                                                        )}
                                                        {t.isArchived && (
                                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                                                                <Archive className="h-3 w-3" /> Archived
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                {canManage && (
                                                    <td className="py-2.5 pr-4">
                                                        <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                                                            {t.isArchived ? (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => runAction(t.id, "unarchive", "The template could not be unarchived.")}
                                                                    disabled={busy}
                                                                    className="flex items-center gap-1 px-2.5 py-1.5 border border-border rounded-md text-xs text-content-secondary hover:text-content-primary hover:bg-theme-hover disabled:opacity-50"
                                                                >
                                                                    <ArchiveRestore className="h-3.5 w-3.5" /> Unarchive
                                                                </button>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => openEditor(t)}
                                                                        disabled={busy || !!editor}
                                                                        className="flex items-center gap-1 px-2.5 py-1.5 border border-border rounded-md text-xs text-content-secondary hover:text-content-primary hover:bg-theme-hover disabled:opacity-50"
                                                                    >
                                                                        <Pencil className="h-3.5 w-3.5" /> Edit
                                                                    </button>
                                                                    {!t.isDefault && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => runAction(t.id, "set-default", "The default template could not be changed.")}
                                                                            disabled={busy}
                                                                            className="flex items-center gap-1 px-2.5 py-1.5 border border-border rounded-md text-xs text-content-secondary hover:text-content-primary hover:bg-theme-hover disabled:opacity-50"
                                                                        >
                                                                            <Star className="h-3.5 w-3.5" /> Set default
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => runAction(t.id, "archive", "The template could not be archived.")}
                                                                        disabled={busy}
                                                                        className="flex items-center gap-1 px-2.5 py-1.5 border border-border rounded-md text-xs text-content-secondary hover:text-red-600 hover:bg-theme-hover disabled:opacity-50"
                                                                    >
                                                                        <Archive className="h-3.5 w-3.5" /> Archive
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
                )}
            </div>
        </div>
    );
};

export default WorkflowTemplatesPage;
